import type { CSSProperties } from "react";
import { CATALOG_PAGE_LIMIT_MAX } from "@/features/catalog/stores/catalogStore";
import { resolveCatalogFontScaleValue } from "@/features/catalog/utils/catalogAppearance";

interface CatalogSkeletonProps {
  columns: number;
  rows: number;
  imageSize: number;
  textLines: number;
  fontScale: number;
  variant: "mobile" | "desktop";
}

const CATALOG_SKELETON_CELL_KEYS = Array.from(
  { length: CATALOG_PAGE_LIMIT_MAX },
  (_, index) => `catalog-skeleton-cell-${index}`,
);
const CATALOG_SKELETON_TEXT_LINE_KEYS = ["first", "second", "third"] as const;
const MOBILE_CATALOG_SKELETON_MAX_ROWS = 6;

function SkeletonBar({
  className,
  style,
}: {
  className: string;
  style?: CSSProperties;
}): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      className={`block rounded bg-muted ${className}`}
      style={style}
    />
  );
}

function CatalogSkeletonCard({
  imageSize,
  textLines,
  variant,
}: Pick<
  CatalogSkeletonProps,
  "imageSize" | "textLines" | "variant"
>): React.ReactElement {
  const textLineKeys = CATALOG_SKELETON_TEXT_LINE_KEYS.slice(0, textLines);

  if (variant === "desktop") {
    return (
      <article className="desktop-catalog-item" aria-hidden="true">
        <div
          className="flex flex-col"
          style={{
            gap: "var(--catalog-space-2)",
            padding: "var(--catalog-space-2)",
          }}
        >
          <figure className="desktop-catalog-thumb">
            <div className="h-full w-full bg-muted" />
          </figure>
          {textLines > 0 && (
            <div className="desktop-catalog-text flex flex-col">
              {textLineKeys.map((lineKey, index) => (
                <SkeletonBar
                  key={lineKey}
                  className={`h-[1.5em] ${index === textLines - 1 ? "w-2/3" : "w-full"}`}
                />
              ))}
            </div>
          )}
          <p className="desktop-catalog-count flex items-center gap-1">
            <SkeletonBar className="h-3 w-8" />
            <SkeletonBar className="ml-auto h-4 w-4" />
          </p>
        </div>
        <footer className="desktop-catalog-footer" aria-hidden="true">
          <ul className="catalog-tags desktop-catalog-tags m-0 flex list-none gap-1 p-0">
            <SkeletonBar className="h-[var(--catalog-tags-height)] w-12" />
          </ul>
        </footer>
      </article>
    );
  }

  return (
    <article className="group relative overflow-hidden rounded-lg border border-transparent bg-card/50">
      <figure
        className="catalog-thumbnail-shell"
        style={{
          width: `min(100%, ${imageSize}px)`,
          maxHeight: `${imageSize}px`,
        }}
      >
        <div
          className="catalog-configured-thumb aspect-square w-full bg-muted"
          style={{ maxHeight: `${imageSize}px` }}
        />
      </figure>
      {textLines > 0 && (
        <header className="flex items-start px-2 pt-2 mb-2">
          <div className="catalog-item-text flex min-w-0 flex-1 flex-col gap-[0.25em] text-xs leading-tight">
            {textLineKeys.map((lineKey, index) => (
              <SkeletonBar
                key={lineKey}
                className={`h-[1em] ${index === textLines - 1 ? "w-2/3" : "w-full"}`}
              />
            ))}
          </div>
        </header>
      )}
      <footer className="catalog-item-tags-row flex items-start gap-1 px-2 pb-2">
        <SkeletonBar className="h-3 w-12" />
        <SkeletonBar className="ml-auto h-4 w-4" />
      </footer>
    </article>
  );
}

export function CatalogSkeleton({
  columns,
  rows,
  imageSize,
  textLines,
  fontScale,
  variant,
}: CatalogSkeletonProps): React.ReactElement {
  const columnCount = Math.max(1, Math.floor(columns));
  const configuredRowCount = Math.max(1, Math.floor(rows));
  const rowCount =
    variant === "desktop"
      ? Math.min(configuredRowCount, 1)
      : Math.min(configuredRowCount, MOBILE_CATALOG_SKELETON_MAX_ROWS);
  const itemCount = Math.min(CATALOG_PAGE_LIMIT_MAX, columnCount * rowCount);
  const cellKeys = CATALOG_SKELETON_CELL_KEYS.slice(0, itemCount);

  const grid =
    variant === "desktop" ? (
      <section className="desktop-catalog-grid-wrap" aria-hidden="true">
        <div
          className="desktop-catalog-grid"
          style={
            {
              "--catalog-columns": columns,
              "--catalog-image-size": `${imageSize}px`,
              "--catalog-font-scale": resolveCatalogFontScaleValue(fontScale),
            } as CSSProperties
          }
        >
          {cellKeys.map((cellKey) => (
            <CatalogSkeletonCard
              key={cellKey}
              imageSize={imageSize}
              textLines={textLines}
              variant={variant}
            />
          ))}
        </div>
      </section>
    ) : (
      <section
        aria-label="スレッドカタログ"
        aria-hidden="true"
        className="grid items-start gap-2 p-2 pb-2 grid-flow-dense"
        style={
          {
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            "--catalog-font-scale": resolveCatalogFontScaleValue(fontScale),
          } as CSSProperties
        }
      >
        {cellKeys.map((cellKey) => (
          <CatalogSkeletonCard
            key={cellKey}
            imageSize={imageSize}
            textLines={textLines}
            variant={variant}
          />
        ))}
      </section>
    );

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={
        variant === "desktop" ? "h-[200px] overflow-hidden" : undefined
      }
    >
      <span className="sr-only">カタログを読み込み中...</span>
      {grid}
    </div>
  );
}
